import {
  CandidateDisposition,
  MediaDetailPrediction,
} from "../common/ObjectTypes";
import { DetailBox } from "./mediaDetailComposer";

/** A pending species choice: the taxon Accept will commit, and its name. */
export type CandidateSelection = { taxonId: number; displayName: string };

/**
 * One distinct candidate species on a bounding box (AKL-777), with every model
 * that proposed it.
 */
export type CandidateSpecies = {
  /** Stable row identity: `taxon:<id>` when resolved, else `label:<raw>`. */
  key: string;
  /** Null for an unmapped candidate carrying no committable taxon. */
  taxonId: number | null;
  /** Resolved species name; falls back to the raw label when unresolved. */
  displayName: string;
  /** The raw model output, for the `unmapped` row's copy. */
  rawLabel: string;
  disposition: CandidateDisposition;
  /** Highest confidence any model gave this species — the headline figure. */
  maxScore: number;
  /** Each contributing model and its own score, highest first. */
  models: { modelName: string; score: number }[];
};

/**
 * Group a box's candidate labels into one row per distinct species (AKL-777).
 *
 * De-duplicating by **taxon** is the whole point. Candidates repeat across
 * models — SpeciesNet says Warthog 62%, DeepFaune also says Warthog at 24% —
 * and grouping by model instead would give two selectable rows with one
 * identical outcome.
 *
 * Two rules carry the correctness of this ticket:
 *
 * 1. **A label with no `disposition` is dropped, exactly like a `suppressed`
 *    one.** An API predating AKL-777 cannot tell us that an excluded
 *    alternative is excluded, and treating unknown as selectable would let a
 *    reviewer commit an observation to a taxon the project has explicitly
 *    excluded — the correctness bug this ticket exists to close. Fail closed.
 * 2. **A row is `visible` if ANY contributing label is.** A taxon one model can
 *    commit and another cannot is still committable.
 * 3. **A `visible` row with no taxon is downgraded, not offered.** Selecting a
 *    row is what tells Accept which taxon to commit, so a selectable row with
 *    nothing to commit is worse than no row. The API is not supposed to send
 *    that combination — api#567 changed its default to fail closed for exactly
 *    this reason — but the client must not depend on that to stay safe.
 *
 * @param predictions every label-bearing prediction on the box
 * @param labelPredictionIds which of them to include; omit for all of them
 * @returns one row per species, by max confidence descending
 */
export const buildCandidateSpecies = (
  predictions: MediaDetailPrediction[],
  labelPredictionIds?: number[],
): CandidateSpecies[] => {
  const wanted =
    labelPredictionIds && labelPredictionIds.length > 0
      ? new Set(labelPredictionIds)
      : null;
  const byKey = new Map<string, CandidateSpecies>();

  for (const prediction of predictions) {
    if (wanted && !wanted.has(prediction.prediction_id)) continue;
    const modelName = prediction.ai_model?.name ?? "";
    for (const label of prediction.labels) {
      // Rule 1: no disposition is as untrusted as an explicit suppression.
      if (label.disposition == null || label.disposition === "suppressed") {
        continue;
      }
      const taxonId = label.resolved_taxon_id ?? null;
      // An unresolved candidate keys by its raw label so two different unmapped
      // labels stay two rows instead of collapsing into one.
      const key =
        taxonId != null ? `taxon:${taxonId}` : `label:${label.raw_label}`;
      const existing = byKey.get(key);
      if (existing == null) {
        byKey.set(key, {
          key,
          taxonId,
          displayName: label.resolved_display_name || label.raw_label,
          rawLabel: label.raw_label,
          disposition: label.disposition,
          maxScore: label.score,
          models: modelName ? [{ modelName, score: label.score }] : [],
        });
        continue;
      }
      existing.maxScore = Math.max(existing.maxScore, label.score);
      // Rule 2: one model being able to commit it is enough.
      if (label.disposition === "visible") existing.disposition = "visible";
      if (modelName) {
        const seen = existing.models.find((m) => m.modelName === modelName);
        if (seen == null) {
          existing.models.push({ modelName, score: label.score });
        } else {
          seen.score = Math.max(seen.score, label.score);
        }
      }
    }
  }

  const rows = Array.from(byKey.values());
  for (const row of rows) {
    row.models.sort((a, b) => b.score - a.score);
    // Rule 3: a selectable row must have something to commit.
    if (row.disposition === "visible" && row.taxonId == null) {
      row.disposition = "unmapped";
    }
  }
  // Max confidence descending, with deterministic tie-breaks so the list never
  // reorders between renders (which would move a row under the reviewer's
  // cursor mid-choice).
  rows.sort(
    (a, b) =>
      b.maxScore - a.maxScore ||
      a.displayName.localeCompare(b.displayName) ||
      a.key.localeCompare(b.key),
  );
  return rows;
};

/**
 * Whether a reviewer may commit this row — the single definition of
 * "selectable", so the popover's per-row rendering and its radio-group gating
 * cannot disagree.
 *
 * The taxon check is redundant with `buildCandidateSpecies`'s Rule 3, which
 * downgrades a visible row carrying no taxon. It is kept because this predicate
 * is what decides whether a radio is offered, and a radio that commits nothing
 * is the failure worth being belt-and-braces about.
 * @param row a row from `buildCandidateSpecies`
 * @returns true when the row can be committed
 */
export const isSelectableCandidate = (row: CandidateSpecies): boolean =>
  row.disposition === "visible" && row.taxonId != null;

/**
 * The rows a reviewer may actually commit, so a caller can tell whether there
 * is anything to pick without re-deriving the selectability rule.
 * @param rows rows from `buildCandidateSpecies`
 * @returns only the selectable ones
 */
export const selectableCandidates = (
  rows: CandidateSpecies[],
): CandidateSpecies[] => rows.filter(isSelectableCandidate);

/**
 * Relabel any box the reviewer has picked a species for (AKL-777), so the image
 * shows what Accept would commit **before** they commit it.
 *
 * Applied over the composed boxes rather than inside `composeDetailBoxes`,
 * because selection only exists on the full-screen surface — the grid and film
 * strip compose from the same function and have no popover to pick in.
 *
 * Returns the input array unchanged when nothing is selected, so the common
 * case allocates nothing and downstream memos do not invalidate.
 * @param boxes composed overlay boxes
 * @param selection pending choices, keyed by box id
 * @returns boxes with the picked species' name on any box that has one
 */
export const applyCandidateSelection = (
  boxes: DetailBox[],
  selection: Record<string, CandidateSelection>,
): DetailBox[] => {
  if (Object.keys(selection).length === 0) return boxes;
  return boxes.map((box) => {
    const picked = selection[box.id];
    return picked ? { ...box, label: picked.displayName } : box;
  });
};

/**
 * Whether this payload carries per-label dispositions at all (AKL-777).
 *
 * Distinguishes "an API that predates per-label dispositions" from "dispositions
 * exist and every candidate was suppressed". `buildCandidateSpecies` returns an
 * empty list for BOTH — it fails closed on a missing disposition — and the two
 * want opposite treatments: the first should fall back to the raw-label list the
 * popover used to show, the second should render nothing, because a box whose
 * only candidates are excluded genuinely has nothing to offer.
 * @param predictions the box's label-bearing predictions
 * @returns true when at least one label states a disposition
 */
export const hasCandidateDispositions = (
  predictions: MediaDetailPrediction[],
): boolean =>
  predictions.some((p) => p.labels.some((l) => l.disposition != null));
